import React, { useState, useMemo, useEffect } from "react"
import Button from "@mui/material/Button"
import Box from "@mui/material/Box"
import Modal from "@mui/material/Modal"
import { IconButton, TextField, Tooltip } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import dayjs from "dayjs"
import { MaterialReactTable, useMaterialReactTable } from "material-react-table"
import normalFormatDate from "../../utils/date/DDMMYYYFormatDate"
import { MRT_Localization_ES } from "material-react-table/locales/es"
import CustomBackdrop from "../../../components/CustomBackdrop"
import GrabarIcon from "../../../assets/iconos/Grabar.ico"
import RegresarIcon from "../../../assets/iconos/Regresar.ico"

const styles = {
  modalStyle: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
    overflow: "scroll",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    spacing: "10px",
  },
  closeStyle: {
    cursor: "pointer",
    marginLeft: "95%",
  },
  componentSpacing: {
    marginBottom: "20px", // Espacio entre componentes
  },
}

const TableLotes = ({ data, onCantidadChange, initialLote }) => {
  const columns = useMemo(
    () => [
      {
        accessorKey: "artlote",
        header: "Lote",
        enableEditing: false,
        size: 80,
      },
      {
        accessorKey: "artfecfab",
        header: "Fecha de Elaboración",
        Cell: ({ cell }) => <div>{normalFormatDate(cell.getValue())}</div>,
      },
      {
        accessorKey: "artfecven",
        header: "Fecha de Vencimiento",
        Cell: ({ cell }) => <div>{normalFormatDate(cell.getValue())}</div>,
      },
      {
        accessorKey: "artcantlote",
        header: "Stock",
      },
      {
        accessorKey: "cantidadADespachar",
        header: "Cantidad",
        Cell: ({ row }) => {
          const stockLote = row.original.artcantlote

          return (
            <IntegerInput
              value={parseFloat(row.original.cantidadADespachar).toFixed(2)}
              setValue={(newCantidad) => {
                // Permitir que el valor del input sea vacío ("") para poder borrar
                if (newCantidad === "" || newCantidad === null) {
                  row.original.cantidadADespachar = null // Se puede poner null o 0 según la lógica deseada
                  onCantidadChange(row.index, null) // Actualizar con null o un valor por defecto
                } else {
                  // Convertir el valor ingresado a número antes de asignarlo
                  // const cantidadNumerica = Number(newCantidad)

                  if (newCantidad > stockLote) {
                    alert(`La cantidad ingresada no puede ser mayor que el stock disponible (${stockLote}).`)
                  } else {
                    row.original.cantidadADespachar = newCantidad
                    onCantidadChange(row.index, newCantidad) // Actualizar la cantidad si es válida
                  }
                }
              }}
            />
          )
        },
      },
    ],
    [onCantidadChange],
  )

  const table = useMaterialReactTable({
    localization: { ...MRT_Localization_ES },
    columns,
    data,
    getRowId: (row) => row.codigoLote,
    enableRowActions: false,
  })

  return <MaterialReactTable table={table} />
}

const IntegerInput = ({ value, setValue, disabled }) => {
  const handleChange = (event) => {
    const newValue = event.target.value.replace(/\D/g, "") // Eliminar todos los caracteres que no sean dígitos
    setValue(newValue ? Number(newValue) : "") // Si no hay valor, establece una cadena vacía
  }

  return (
    <TextField
      fullWidth
      variant="outlined"
      type="text" // Cambia el tipo a "text" para permitir valores vacíos
      value={value} // Si el valor es 0, muestra una cadena vacía
      onChange={handleChange}
      disabled={disabled}
    />
  )
}

const Lotes = ({ maxCantidadEmbalaje, setLotesInProducto, cantidadLoteInitial, initialLotes }) => {
  const [open, setOpen] = useState(false) // modal
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)
  const [isLoadingSearchLote, setIsLoadingSearchLote] = useState(false)
  const [statusBlockingInput, setStatusBlockingInput] = useState(false)

  const [fechaElaboracionLote, setFechaElaboracionLote] = useState(dayjs(new Date()))
  const [fechaVencimientoLote, setfechaVencimientoLote] = useState(dayjs(new Date()))
  const [codigoLote, setCodigoLote] = useState("")
  const [cantidadLote, setCantidadLote] = useState(parseFloat(0))

  const [lotesData, setLotesData] = useState(initialLotes)

  const [data, setData] = useState(initialLotes)
  const handleCantidadChange = (index, newCantidad) => {
    const updatedData = [...data]
    updatedData[index].cantidadADespachar = newCantidad
    setData(updatedData)
  }

  useEffect(() => {
    const cantidadTotal = data.reduce((acc, lote) => acc + (parseFloat(lote.cantidadADespachar) || 0), 0)
    setCantidadLote(parseFloat(cantidadTotal)) // Actualiza el valor del input "Cantidad a despachar"
  }, [data]) // El efecto se dispara cada vez que cambian los datos de lotes

  // UseEffect para cargar los lotes iniciales cuando se reciben por props
  useEffect(() => {
    if (initialLotes) {
      setLotesData(initialLotes)
      console.log(initialLotes)
      setCantidadLote(maxCantidadEmbalaje)
    }
  }, [initialLotes])

  const limpiarInputs = () => {
    setCodigoLote("")
    setCantidadLote("")
    unblockInputs()
  }

  const limpiarModal = () => {
    setFechaElaboracionLote(dayjs(new Date()))
    setfechaVencimientoLote(dayjs(new Date()))
    setCodigoLote("")
    setCantidadLote("")
    setLotesData([]) // Limpiar los datos en el modal
    unblockInputs()
  }

  const blockInputs = () => {
    setStatusBlockingInput(true)
  }

  const unblockInputs = () => {
    setStatusBlockingInput(false)
  }

  const saveLotes = () => {
    const cantidadTotalLotesRegistrados = lotesData.reduce((acc, lote) => acc + parseFloat(lote.cantidadADespachar), 0)

    if (cantidadTotalLotesRegistrados !== maxCantidadEmbalaje) {
      alert(`La cantidad ingresada por lote ${cantidadTotalLotesRegistrados} es diferente de ${maxCantidadEmbalaje}`)
      return
    }

    setLotesInProducto(lotesData)
    handleClose()
  }

  return (
    <span style={{ flexGrow: 1 }}>
      <CustomBackdrop isLoading={isLoadingSearchLote} zIndex={999999} />
      <Button
        variant="text"
        color="primary"
        fullWidth
        style={{
          backgroundColor: "#4DB1E3",
          color: "white",
          fontSize: "1.1rem",
          letterSpacing: "1rem",
          fontWeight: "bolder",
        }}
        onClick={() => {
          // limpiarModal()
          handleOpen()
        }}
      >
        ...
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={styles.modalStyle}>
          <IconButton style={styles.closeStyle} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
          <form
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <Box sx={{ marginBottom: "10px" }}>
              <span>Cantidad a despachar:</span>
              <IntegerInput value={cantidadLote} setValue={setCantidadLote} disabled={true} />
            </Box>
          </form>

          <TableLotes
            data={lotesData}
            setData={setLotesData}
            onCantidadChange={handleCantidadChange}
            initialLote={cantidadLoteInitial}
          />

          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Button onClick={saveLotes}>
              <img src={GrabarIcon} alt="Grabar" style={{ width: "40px" }} />
            </Button>
            <Button color="primary" onClick={limpiarInputs}>
              <img src={RegresarIcon} alt="Cerrar" style={{ width: "40px" }} />
            </Button>
          </Box>
        </Box>
      </Modal>
    </span>
  )
}

export default Lotes
